import * as React from 'react'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { useSelector, useDispatch } from 'react-redux'
import { styled } from '@mui/material/styles'
import { Grid, Box, Button, IconButton, Stack } from '@mui/material'
import { Typography as MIUTypography, InputAdornment } from '@mui/material'
import { Lock, Visibility, VisibilityOff } from '@mui/icons-material'
import { setIsStored } from '../../redux/slices/Alerts'
import { useParams, useNavigate } from 'react-router-dom'
import LoginIcon from '@mui/icons-material/Login'
import ImagenBackground from '../../assets/images/arbol-ec/fondos/DJI_05.jpeg'
import { LOGIN_ROUTE } from '../../config/routes'
import { resetPassword } from '../../services'
import _ from 'lodash'

import { schema } from '../../utils/schemaValidation'
import AlertCustom from '../../components/Alerts'

import Textfield from '../../components/FormsUI/Textfield'

const Typography = styled(MIUTypography)(({ theme, istitle }) => ({
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1.125em',
  fontWeight: istitle ? 700 : 450,
  lineHeight: '24px',
  color: istitle ? theme.palette.text.primary : theme.palette.text.secondary,
  textAlign: 'center',
}))

const ButtonCustom = styled(Button)(({ theme }) => ({
  height: '2em',
  backgroundColor: theme.palette.secondary.main,
  textTransform: 'capitalize',
  fontFamily: theme.typography.fontFamily,
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '22px',
  color: theme.palette.common.white,
  fontSize: '16px',
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
  '&:active': {
    boxShadow: 'none',
    backgroundColor: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
  },
}))

const styleStack = {
  justifyContent: 'center',
  direction: 'column',
  alignItems: 'center',
  py: 2,
  px: '8vw',
  height: '100%',
  spacing: 2,
  width: '100%',
}

const boxStyle = {
  minWidth: '300px',
  maxWidth: '500px',
  bgcolor: 'background.paper',
  boxShadow: 14,
  pb: '50px',
  px: '25px',
  textAlign: 'center',
  borderRadius: '14px',
}

const ProfilePage = () => {
  const dispatch = useDispatch()
  const alertRef = React.useRef()
  const navigate = useNavigate()
  const { token } = useParams()

  const [showPassword, setShowPassword] = React.useState(false)
  const _state = useSelector(state => state.drawerLeft)
  const { isStored } = useSelector(state => state.alertsGlobal)

  const formatToken = token ? _.split(token, '=')?.[1] : ''
  const TOKEN = 'Bearer ' + formatToken

  const INITIAL_FORM_STATE = { password_alt: '', password_confirmation_alt: '' }
  const FORM_VALIDATION = Yup.object().shape({
    ...schema.PASSWORD_ALT,
    ...schema.PASSWORD_CONFIRMATION_ALT,
  })

  const styleBox = {
    backgroundImage: `url(${ImagenBackground})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    marginLeft: _state.open ? _state.drawerWidth : 0,
    marginTop: _state.drawerHeight,
    height: _state.contentBox,
    width: 'inherit',
  }

  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleMouseDownPassword = event => event.preventDefault()

  const handleClickReset = async ({ password_alt: contrasena }) => {
    let resultSave = { stored: true }
    const response = await resetPassword({ contrasena }, TOKEN)
    if (!response?.update) {
      resultSave = { stored: false, message: response?.message }
    }
    return resultSave
  }

  const handleClickLogin = () => navigate(LOGIN_ROUTE)

  if (isStored?.stored)
    return (
      <Box component="div" style={styleBox}>
        <Stack {...styleStack}>
          <Box pt={3}>
            <Typography
              sx={{ fontWeight: 650, color: theme => theme.palette.background.paper }}>
              Contraseña actualizada
            </Typography>
          </Box>
          <Box pt={1}>
            <Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<LoginIcon />}
                onClick={handleClickLogin}
                sx={{
                  backgroundColor: 'primary.main',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}>
                Iniciar Sesión
              </Button>
            </Box>
          </Box>
        </Stack>
      </Box>
    )

  return (
    <Box component="div" style={styleBox}>
      <Stack {...styleStack}>
        <Box sx={boxStyle}>
          <Box pt={3}>
            <Typography istitle="true">Restablecer contraseña</Typography>
          </Box>
          <Box pt={3}>
            <Formik
              initialValues={{ ...INITIAL_FORM_STATE }}
              validationSchema={FORM_VALIDATION}
              onSubmit={async values => {
                const itsStored = await handleClickReset(values)
                dispatch(setIsStored(itsStored))
                if (!itsStored?.stored) alertRef?.current?.openAlert(true)
              }}>
              <Form>
                <Grid container spacing={2} sx={{ pt: 0, width: '100%', ml: 0 }}>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="password_alt"
                      label="Contraseña"
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end">
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Contraseña"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <Textfield
                      name="password_confirmation_alt"
                      label="Confirmar contraseña"
                      type={showPassword ? 'text' : 'password'}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end">
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Confirmar contraseña"
                    />
                  </Grid>
                  <Grid item xs={12} style={{ paddingLeft: 0 }}>
                    <ButtonCustom
                      variant="contained"
                      fullWidth
                      type="submit"
                      sx={{ backgroundColor: theme => theme.palette.primary.main }}>
                      Restablecer
                    </ButtonCustom>
                  </Grid>
                </Grid>
              </Form>
            </Formik>
          </Box>
        </Box>
      </Stack>
      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.stored !== true ? isStored?.message : 'Guardado exitosamente.'}
      </AlertCustom>
    </Box>
  )
}

export default ProfilePage
